import React, { useState, useMemo } from "react";
import { useContent, useLang } from "../i18n/LanguageContext";
import { ArrowRight } from "lucide-react";

export default function Calculator({ onOpenAccount }) {
  const { lang } = useLang();
  const c = useContent();
  const conditions = c.conditions;
  const t = c.calculator;
  const fmt = (n) =>
    new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n);

  const [amount, setAmount] = useState(25000);
  const [termIdx, setTermIdx] = useState(2);
  const [years, setYears] = useState(10);

  const row = conditions.rows[termIdx];
  const rate = row.rate / 100;

  const termInterest = useMemo(() => amount * rate * (row.months / 12), [amount, rate, row.months]);
  const payout = amount + termInterest;
  const finalCapital = useMemo(() => amount * Math.pow(1 + rate, years), [amount, rate, years]);
  const totalInterest = finalCapital - amount;

  const points = useMemo(() => {
    return Array.from({ length: years + 1 }, (_, y) => ({
      y,
      value: amount * Math.pow(1 + rate, y),
    }));
  }, [amount, rate, years]);

  const maxV = points[points.length - 1].value;
  const w = 560, h = 200, pad = 8;
  const path = points
    .map((p, i) => {
      const x = pad + (i / years) * (w - pad * 2);
      const yy = h - pad - (p.value / maxV) * (h - pad * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${yy.toFixed(1)}`;
    })
    .join(" ");
  const area = `${path} L${w - pad},${h - pad} L${pad},${h - pad} Z`;

  return (
    <section id="rechner" className="py-20 lg:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl reveal">
          <div className="gold-rule mb-6" />
          <h2 className="font-serif-lux text-4xl lg:text-5xl text-[#16233d] font-semibold">{t.title}</h2>
          <p className="mt-4 text-[#5c6577] text-lg">
            {t.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mt-12">
          {/* Controls */}
          <div className="rounded-2xl border border-[#efe9dd] bg-[#faf8f4] p-8">
            <label className="text-sm text-[#3a4258] font-medium">{t.amount}</label>
            <div className="font-serif-lux text-4xl text-[#16233d] mt-1 mb-3">{fmt(amount)}</div>
            <input
              type="range" min={5000} max={500000} step={1000} value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="w-full accent-[#b08d57] cursor-pointer"
            />

            <div className="mt-8">
              <label className="text-sm text-[#3a4258] font-medium">{t.term}</label>
              <div className="flex flex-wrap gap-2 mt-3">
                {conditions.rows.map((r, i) => (
                  <button
                    key={r.term}
                    onClick={() => setTermIdx(i)}
                    className={`px-4 py-2 rounded-md text-sm border transition-all ${
                      i === termIdx
                        ? "bg-[#16233d] text-white border-[#16233d]"
                        : "bg-white text-[#3a4258] border-[#e7e2d8] hover:border-[#b08d57]"
                    }`}
                  >
                    {r.term}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <div className="flex justify-between">
                <label className="text-sm text-[#3a4258] font-medium">{t.horizon}</label>
                <span className="text-sm font-medium text-[#16233d]">{years} {t.years}</span>
              </div>
              <input
                type="range" min={1} max={30} step={1} value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                className="w-full accent-[#b08d57] cursor-pointer mt-3"
              />
            </div>

            <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-[#e7e2d8]">
              <div>
                <div className="text-xs text-[#8a8f9c]">{t.ratePa}</div>
                <div className="font-serif-lux text-2xl text-[#b08d57]">{row.rate.toFixed(2)}%</div>
              </div>
              <div>
                <div className="text-xs text-[#8a8f9c]">{t.interestTerm}</div>
                <div className="font-serif-lux text-2xl text-[#16233d]">{fmt(termInterest)}</div>
              </div>
              <div>
                <div className="text-xs text-[#8a8f9c]">{t.payout}</div>
                <div className="font-serif-lux text-2xl text-[#16233d]">{fmt(payout)}</div>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="rounded-2xl border border-[#efe9dd] bg-[#16233d] p-8 text-white flex flex-col">
            <div className="text-sm text-white/60">{t.capitalAfter} {years} {t.years}</div>
            <div className="font-serif-lux text-5xl text-[#d3b077] mt-1">{fmt(finalCapital)}</div>
            <div className="text-xs text-white/50 mt-2">{t.developmentReinvest}</div>

            <div className="mt-6 flex-1">
              <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d3b077" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#d3b077" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d={area} fill="url(#grad)" />
                <path d={path} fill="none" stroke="#d3b077" strokeWidth="2.5" />
              </svg>
              <div className="flex justify-between text-[10px] text-white/40 mt-2">
                <span>0J</span><span>{Math.round(years / 2)}J</span><span>{years}J</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10 text-sm text-white/70">
              {t.totalInterest}: <span className="text-[#d3b077] font-medium">{fmt(totalInterest)}</span>
            </div>
            <button onClick={onOpenAccount} className="btn-gold rounded-md px-6 py-3 font-medium mt-5 inline-flex items-center justify-center gap-2">
              {t.openAccount} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
